import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { OtpModal } from './OtpModal';
import { Spinner } from '../shared/Spinner';
import { validateForm, rules } from '../../utils/validators';

interface LoginSignupCardProps {
  onClose?: () => void;
  className?: string;
}

export const LoginSignupCard: React.FC<LoginSignupCardProps> = ({ onClose, className = '' }) => {
  const { requestOtp, isAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpEmail, setOtpEmail] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate email
    const errors = validateForm({ email }, {
      email: [rules.required('Email'), rules.email]
    });

    if (errors) {
      setError(errors.email);
      return;
    }

    setLoading(true);
    try {
      await requestOtp(email);
      setOtpEmail(email);
      setShowOtpModal(true);
      setEmail('');
    } catch (err: any) {
      setError(err.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSuccess = () => {
    setShowOtpModal(false);
    setOtpEmail('');
    if (onClose) onClose();
  };

  const handleOtpClose = () => {
    setShowOtpModal(false);
    setOtpEmail('');
  };

  if (isAuthenticated) {
    return null;
  }

  return (
    <>
      <div className={`bg-white/20 backdrop-blur-md rounded-2xl p-8 max-w-md mx-auto relative border border-white/30 ${className}`}>
        {/* Close button */}
        {onClose && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-white mb-2">Get Started</h2>
          <p className="text-white/90 text-sm">
            Join our community of builders, makers, and sustainability enthusiasts.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-white/90 mb-2">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent"
              disabled={loading}
              required
            />
          </div>

          {error && (
            <div className="text-red-200 text-sm bg-red-500/20 border border-red-300/30 rounded-lg p-3">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !email.trim()}
            className="w-full bg-white text-primary-600 font-semibold py-3 px-4 rounded-lg hover:bg-white/90 focus:outline-none focus:ring-2 focus:ring-white/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center"
          >
            {loading ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Sending Code...
              </>
            ) : (
              'Send Verification Code'
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-white/70 text-xs">
            We'll send you a 6-digit code to verify your email.
            <br />
            No password required!
          </p>
        </div>
      </div>

      {showOtpModal && (
        <OtpModal
          email={otpEmail}
          onSuccess={handleOtpSuccess}
          onClose={handleOtpClose}
        />
      )}
    </>
  );
};
